import type {
  RtaLinearFit,
  RtaPoint,
} from './rtaCalculations';

export interface GasPvtPoint {
  /**
   * 压力 MPa
   */
  pressure: number;

  /**
   * 气体偏差因子
   */
  z: number;

  /**
   * 气体黏度 mPa·s
   */
  viscosity: number;
}

export interface PseudoPressureRtaPoint
  extends RtaPoint {
  /**
   * Δm =
   * m(Pr) - m(Pwf)
   *
   * 当前单位口径：
   * MPa² / (mPa·s)
   */
  deltaM: number;

  /**
   * Δm / q
   */
  inverseNormalizedPseudoPressure: number;

  /**
   * q / Δm
   */
  normalizedPseudoRate: number;
}

export function cleanGasPvt(
  points: GasPvtPoint[],
): GasPvtPoint[] {
  const valid = points
    .filter(
      (point) =>
        Number.isFinite(
          point.pressure,
        ) &&
        Number.isFinite(
          point.z,
        ) &&
        Number.isFinite(
          point.viscosity,
        ) &&
        point.pressure >= 0 &&
        point.z > 0 &&
        point.viscosity > 0,
    )
    .sort(
      (a, b) =>
        a.pressure -
        b.pressure,
    );

  const result:
    GasPvtPoint[] = [];

  for (
    const point of valid
  ) {
    const previous =
      result[
        result.length - 1
      ];

    if (
      previous &&
      Math.abs(
        previous.pressure -
          point.pressure,
      ) < 1e-9
    ) {
      /*
       * 相同压力点：
       * 后录入值覆盖前值
       */
      result[
        result.length - 1
      ] = point;
    } else {
      result.push(point);
    }
  }

  return result;
}

export function interpolateGasPvt(
  points: GasPvtPoint[],
  pressure: number,
): {
  z: number;
  viscosity: number;
} | null {
  const clean =
    cleanGasPvt(points);

  if (
    clean.length < 2 ||
    pressure <
      clean[0].pressure ||
    pressure >
      clean[
        clean.length - 1
      ].pressure
  ) {
    return null;
  }

  const exact =
    clean.find(
      (point) =>
        Math.abs(
          point.pressure -
            pressure,
        ) < 1e-9,
    );

  if (exact) {
    return {
      z: exact.z,
      viscosity:
        exact.viscosity,
    };
  }

  for (
    let i = 1;
    i < clean.length;
    i += 1
  ) {
    const left =
      clean[i - 1];

    const right =
      clean[i];

    if (
      pressure >
        left.pressure &&
      pressure <
        right.pressure
    ) {
      const fraction =
        (
          pressure -
          left.pressure
        ) /
        (
          right.pressure -
          left.pressure
        );

      return {
        z:
          left.z +
          fraction *
            (
              right.z -
              left.z
            ),

        viscosity:
          left.viscosity +
          fraction *
            (
              right.viscosity -
              left.viscosity
            ),
      };
    }
  }

  return null;
}

/**
 * 实气拟压力差：
 *
 * Δm =
 * 2 ∫[Pwf→Pr]
 * p / (μg Z) dp
 *
 * p: MPa
 * μg: mPa·s
 *
 * 因此这里结果保持统一的
 * 工程相对单位：
 * MPa²/(mPa·s)
 */
export function calculatePseudoPressureDrop(
  pwf: number,
  pr: number,
  pvt: GasPvtPoint[],
): number | null {
  if (
    !Number.isFinite(pwf) ||
    !Number.isFinite(pr) ||
    pr <= pwf ||
    pwf < 0
  ) {
    return null;
  }

  const clean =
    cleanGasPvt(pvt);

  if (
    clean.length < 2
  ) {
    return null;
  }

  /*
   * 禁止盲目外推 PVT。
   */
  if (
    pwf <
      clean[0].pressure ||
    pr >
      clean[
        clean.length - 1
      ].pressure
  ) {
    return null;
  }

  /*
   * 积分节点：
   * Pwf + 区间内已有PVT点 + Pr
   */
  const pressures = [
    pwf,
    ...clean
      .map(
        (point) =>
          point.pressure,
      )
      .filter(
        (pressure) =>
          pressure > pwf &&
          pressure < pr,
      ),
    pr,
  ];

  let integral = 0;

  for (
    let i = 1;
    i <
    pressures.length;
    i += 1
  ) {
    const p0 =
      pressures[i - 1];

    const p1 =
      pressures[i];

    const pvt0 =
      interpolateGasPvt(
        clean,
        p0,
      );

    const pvt1 =
      interpolateGasPvt(
        clean,
        p1,
      );

    if (
      !pvt0 ||
      !pvt1
    ) {
      return null;
    }

    const f0 =
      2 *
      p0 /
      (
        pvt0.viscosity *
        pvt0.z
      );

    const f1 =
      2 *
      p1 /
      (
        pvt1.viscosity *
        pvt1.z
      );

    integral +=
      0.5 *
      (
        f0 + f1
      ) *
      (
        p1 - p0
      );
  }

  return integral;
}

export function buildPseudoPressureRtaPoints(
  points: RtaPoint[],
  pvt: GasPvtPoint[],
): PseudoPressureRtaPoint[] {
  const result:
    PseudoPressureRtaPoint[] =
    [];

  for (
    const point of points
  ) {
    const deltaM =
      calculatePseudoPressureDrop(
        point.pwf,
        point.pr,
        pvt,
      );

    if (
      deltaM === null ||
      deltaM <= 0 ||
      point.q <= 0
    ) {
      continue;
    }

    result.push({
      ...point,

      deltaM,

      inverseNormalizedPseudoPressure:
        deltaM /
        point.q,

      normalizedPseudoRate:
        point.q /
        deltaM,
    });
  }

  return result;
}

export function fitPseudoPressureLinearTrend(
  points:
    PseudoPressureRtaPoint[],
  xType:
    | 'sqrtT'
    | 'sqrtTmb',
): RtaLinearFit | null {
  const valid =
    points.filter(
      (point) =>
        Number.isFinite(
          point[xType],
        ) &&
        Number.isFinite(
          point.inverseNormalizedPseudoPressure,
        ),
    );

  if (
    valid.length < 3
  ) {
    return null;
  }

  const xs =
    valid.map(
      (point) =>
        point[xType],
    );

  const ys =
    valid.map(
      (point) =>
        point.inverseNormalizedPseudoPressure,
    );

  const meanX =
    xs.reduce(
      (sum, value) =>
        sum + value,
      0,
    ) /
    xs.length;

  const meanY =
    ys.reduce(
      (sum, value) =>
        sum + value,
      0,
    ) /
    ys.length;

  let numerator = 0;
  let denominator = 0;

  for (
    let i = 0;
    i < xs.length;
    i += 1
  ) {
    numerator +=
      (
        xs[i] -
        meanX
      ) *
      (
        ys[i] -
        meanY
      );

    denominator +=
      (
        xs[i] -
        meanX
      ) ** 2;
  }

  if (
    Math.abs(
      denominator,
    ) <
    1e-12
  ) {
    return null;
  }

  const slope =
    numerator /
    denominator;

  const intercept =
    meanY -
    slope * meanX;

  const predicted =
    xs.map(
      (x) =>
        intercept +
        slope * x,
    );

  const ssRes =
    ys.reduce(
      (
        sum,
        y,
        index,
      ) =>
        sum +
        (
          y -
          predicted[index]
        ) ** 2,
      0,
    );

  const ssTot =
    ys.reduce(
      (sum, y) =>
        sum +
        (
          y -
          meanY
        ) ** 2,
      0,
    );

  const r2 =
    ssTot <
    1e-12
      ? 1
      : 1 -
        ssRes /
        ssTot;

  const rmse =
    Math.sqrt(
      ssRes /
        ys.length,
    );

  return {
    count:
      valid.length,

    slope,
    intercept,
    r2,
    rmse,

    minX:
      Math.min(...xs),

    maxX:
      Math.max(...xs),
  };
}